import React, { useState } from "react";
import { PlusIcon, XIcon } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../../../../components/ui/accordion";
import { motion, AnimatePresence } from "framer-motion";

const FaqSection = () => {
  const [openItem, setOpenItem] = useState("01");

  const faqItems = [
    {
      id: "01",
      question: "Who is this trip For?",
      answer:
        "Anyone looking to grow personally or professionally — entrepreneurs, students, creators,solo explorers or anyone craving clarity, learning, and meaningful travel.",
    },
    {
      id: "02",
      question: "Do I need to be an expert in the trip’s theme to join?",
      answer:
        "Not at all. Whether you’re exploring a new field or deepening your craft, our mentor-led approach makes it valuable for all levels.",
    },
    {
      id: "03",
      question: "What’s included in the trip cost?",
      answer:
        "Your stay, curated experiences, workshops, mentor sessions, and most meals are covered. Travel to the destination is not included unless mentioned.",
    },
    {
      id: "04",
      question: "How are the groups formed?",
      answer:
        "All trips are invite-only. We review each application to ensure group synergy – aligned values, energy, and intent",
    },
    {
      id: "05",
      question: "How safe are your trips – especially for women?",
      answer:
        "Very. We prioritize safety from day one – verified stays, vetted staff, women-friendly mentors, and 24/7 ground support. Many of our past travelers are solo female participants.",
    },
    {
      id: "06",
      question: "Can I come solo?",
      answer:
        "Absolutely. Most of our participants join solo – and leave with lifelong friends.",
    },
    {
      id: "07",
      question: "Who leads the trip?",
      answer:
        "Each trip is led by an experienced mentor – a founder, creator, coach, or expert from that domain. They guide learning, facilitate discussions, and support you throughout.",
    },
    {
      id: "08",
      question: "Will I actually learn something or is this just a vacation?",
      answer: `Every trip is outcome-oriented – you'll leave with new ideas, practical frameworks, personal breakthroughs, and real skills, not just photos.`,
    },
    {
      id: "09",
      question: "What if I need to cancel?",
      answer:
        "We offer a full refund within 36 hours of booking. Beyond that, our cancellation policy ensures fairness while covering trip costs.",
    },
    {
      id: "10",
      question: "Can you help with flights, visas, or planning?",
      answer:
        "Yes – our team provides guidance, travel checklists, and planning assistance to make your journey smooth from booking to boarding.",
    },
    {
      id: "11",
      question: "What happens after the trip ends?",
      answer:
        "You’ll be added to an alumni community with your batchmates. Many participants go on to collaborate, meet again, or join future experiences.",
    },
    {
      id: "12",
      question: "How can I apply or join?",
      answer:
        "Just click “Request Invite,” fill out the short form, and we’ll get in touch with next steps.",
    },
  ];

  return (
    <section className="w-full bg-[#FFFFFFE6] px-4 py-6">
      <div className="max-w-[1180px] mx-auto">
        {/* FAQ Heading */}
        <h2 className="text-center text-[#0D0D0D] font-bricolage font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight">
          Frequently{" "}
          <span className="text-[#FFAE00] underline">Asked Questions</span>
        </h2>
        <p className="text-center font-bricolage font-bold text-[#063258] text-base sm:text-lg max-w-2xl mx-auto mb-4">
          Still curious? Here’s everything you might be wondering.
        </p>

        <Accordion
          type="single"
          collapsible
          value={openItem}
          onValueChange={(val) => setOpenItem(val)}
          className="w-full"
        >
          {faqItems.map((item) => (
            <AccordionItem
              key={item.id}
              value={item.id}
              className="border-b-[3px] border-[#FFAE00] mb-1 last:mb-0"
            >
              <div className="flex flex-col md:flex-row gap-3 md:gap-[40px] px-2 md:px-[48px] py-3 md:py-6">
                <div className="flex-shrink-0">
                  <span className="font-semibold text-[#FFAE00] text-[24px] md:text-[36px] leading-tight">
                    {item.id}
                  </span>
                </div>

                <div className="flex-1 pt-[2px]">
                  <AccordionTrigger className="w-full flex justify-between items-start cursor-pointer text-left [&>svg]:hidden">
                    <h3 className="font-medium text-[#0D0D0D] text-[16px] md:text-[22px] leading-snug">
                      {item.question}
                    </h3>
                    <div className="w-7 h-7 md:w-10 md:h-10 flex justify-center items-center bg-[#FFAE00] rounded-full">
                      {openItem === item.id ? (
                        <XIcon className="w-[10px] h-[10px] md:w-[12px] md:h-[12px] text-[#0D0D0D]" />
                      ) : (
                        <PlusIcon className="w-[10px] h-[10px] md:w-[12px] md:h-[12px] text-[#0D0D0D]" />
                      )}
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="mt-1 md:mt-2 overflow-hidden">
                    <AnimatePresence initial={false}>
                      {openItem === item.id && (
                        <motion.div
                          key="content"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                          <ul className="list-disc pl-5 space-y-2 font-normal text-[#0D0D0D] text-xs md:text-lg leading-relaxed">
                            {item.answer.split("\n").map((point, index) => (
                              <li key={index}>{point}</li>
                            ))}
                          </ul>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </AccordionContent>
                </div>
              </div>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default FaqSection;
